import React, { useState } from 'react';
import DocumentViewer from './DocumentViewer';

const FILE_ICONS = {
  'application/pdf': '\u{1F4C4}',
  'image/': '\u{1F5BC}\uFE0F',
  'text/': '\u{1F4DD}',
  'application/zip': '\u{1F4E6}',
  'application/vnd.openxmlformats': '\u{1F4CA}',
};

function getIcon(mime) {
  for (const [prefix, icon] of Object.entries(FILE_ICONS)) {
    if (mime && mime.startsWith(prefix)) return icon;
  }
  return '\u{1F4CE}';
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function canViewInline(att) {
  const kind = att?.viewer_kind;
  if (kind && kind !== 'download') return true;
  // Legacy attachments (pre viewer_kind): guess from mime
  const mime = (att?.mime_type || '').toLowerCase();
  if (mime === 'application/pdf' || mime.startsWith('image/') || mime.includes('markdown')) return true;
  const name = (att?.filename || '').toLowerCase();
  return /\.(pdf|png|jpe?g|gif|webp|md|markdown)$/.test(name);
}

export default function AttachmentPanel({ attachments = [], sceneId, apiBase = '' }) {
  const [viewing, setViewing] = useState(null);

  if (!attachments.length) return null;

  return (
    <div style={{
      marginTop: 16, padding: 12,
      border: '1px solid var(--border-color, #e5e7eb)',
      borderRadius: 8, background: 'var(--surface-secondary, #f9fafb)',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 8, color: 'var(--text-secondary, #374151)' }}>
        {'\u{1F4CE}'} Resources ({attachments.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {attachments.map((att) => {
          const viewable = canViewInline(att);
          const downloadHref = `${apiBase}/scenario-os/coach/attachments/download/${sceneId}/${att.attachment_id}`;
          return (
            <div
              key={att.attachment_id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 6,
                background: 'var(--surface-primary, #fff)',
                border: '1px solid var(--border-color, #e5e7eb)',
              }}
            >
              <span style={{ fontSize: '1.25rem' }}>{getIcon(att.mime_type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.title || att.filename}
                </div>
                {att.description && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary, #6b7280)', marginTop: 2 }}>
                    {att.description}
                  </div>
                )}
                {att.conversion_status === 'failed' && (
                  <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 2 }}>
                    Preview conversion failed — download original
                  </div>
                )}
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary, #6b7280)', whiteSpace: 'nowrap' }}>
                {formatSize(att.size_bytes)}
              </span>
              {viewable && (
                <button
                  type="button"
                  onClick={() => setViewing(att)}
                  style={{
                    fontSize: '0.8rem',
                    color: '#fff',
                    background: 'var(--accent-color, #3b82f6)',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 10px',
                    cursor: 'pointer',
                  }}
                >
                  View
                </button>
              )}
              <a
                href={downloadHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.8rem', color: 'var(--accent-color, #3b82f6)', textDecoration: 'none' }}
                title="Download original"
              >
                {'\u2193'}
              </a>
            </div>
          );
        })}
      </div>
      {viewing && (
        <DocumentViewer
          sceneId={sceneId}
          attachment={viewing}
          apiBase={apiBase}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
